import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { fade } from '@core/animations/fade.animation';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Apollo } from 'apollo-angular';
import { find, isNull, isUndefined } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { sentry } from 'sentry';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface OutlinedSelectOption {
  text: string;
  value: unknown;
}

interface QueryResult {
  id: string;
  name: string;
}

@Component({
  selector: 'fc-outlined-select',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './fc-outlined-select.component.html',
  styleUrls: ['./fc-outlined-select.component.scss'],
  animations: [fade]
})
export class FcOutlinedSelectComponent implements OnInit, OnDestroy {
  @Input()
  control: FormControl;

  @Input()
  required: boolean;

  @Input()
  label: string;

  @Input()
  alreadySelected: string[] = [];

  @Input()
  public searchFn: (searchTerm: string, limit: number, offset: number) => Observable<QueryResult[]>;

  @Input()
  public predefinedOptions: OutlinedSelectOption[] = [];

  @Output()
  select = new EventEmitter<string>();

  private searchTerm = '';
  private reachedBottom = false;
  private hasMoreOptions = true;
  private optionsOffset = 0;
  private readonly optionsLimit = 10;
  private readonly removeSubscriptions = new Subject<void>();

  public showOptions = false;
  public selectedValue: string = null;
  public isOptionsAbove = false;
  public isLoading = false;
  public textInputForm = new FormControl(null);
  public options: OutlinedSelectOption[] = [];

  constructor(
    private elementRef: ElementRef,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    public readonly apollo: Apollo
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  @HostListener('document:click', ['$event'])
  public onClickOutside(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showOptions = false;
    }
  }

  ngOnInit(): void {
    this.setSelectedValue();

    if (this.predefinedOptions.length) {
      this.options = this.predefinedOptions;
      return;
    }

    this.searchFn(this.searchTerm, this.optionsLimit, this.optionsOffset)
      .subscribe((options: QueryResult[]) => {
        this.options = this.mapQueryResultToOutlinedSelectOption(options);
      });

    this.textInputForm.valueChanges
      .pipe(
        takeUntil(this.removeSubscriptions),
        debounceTime(500),
        tap(() => this.resetValues()),
        map((value) => value || ''),
        filter((value) => typeof value === 'string'),
        map((value) => value.trim()),
        tap((searchTerm: string) => {
          this.searchTerm = searchTerm;
          this.isLoading = true;
        }),
        switchMap(() => this.searchFn(this.searchTerm, this.optionsLimit, this.optionsOffset)),
        tap(() => this.isLoading = false)
      ).subscribe((options: QueryResult[]) => {
        this.options = this.mapQueryResultToOutlinedSelectOption(options);
      });

    this.control.valueChanges.subscribe(() => {
      this.setSelectedValue();
    });
  }

  ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  public selectOption(option: OutlinedSelectOption): void {
    if (!this.alreadySelected.includes(option.text)) {
      this.control.setValue(option.value);
      this.setSelectedValue();
    } else {
      this.control.setValue(null);
      this.setSelectedValue();
    }

    this.showOptions = false;
  }

  private setSelectedValue(): void {
    const { value } = this.control;
    if (isNull(value) || isUndefined(value)) {
      this.selectedValue = null;
      return;
    }

    if (!value.id) {
      const existingValue = find(this.predefinedOptions, {
        value: value.id ? { id: value.id } : value
      });

      this.selectedValue = (existingValue && existingValue.text) || null;
      this.select.emit();

      return;
    }

    this.selectedValue = value.name;
    this.select.emit();
  }

  public onBlur(): void {
    this.textInputForm.setValue('');
  }

  public clearTextAndSelection(): void {
    this.control.setValue(null);
    this.textInputForm.setValue(null);
  }

  public openOptionsList(): void {
    if (this.control.disabled) { return; }

    const maxOptions = this.options.length < 7 ? this.options.length : 7;
    const optionsListHeight = 48 * maxOptions + 16;

    const componentRect = this.elementRef.nativeElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const spaceAbove = componentRect.top;
    const spaceBelow = windowHeight - componentRect.bottom;

    this.isOptionsAbove = spaceBelow < optionsListHeight || spaceBelow < spaceAbove;
    this.showOptions = true;
  }

  public handleScroll(event: Event): void {
    const ulElement = event.target as HTMLUListElement;

    const maxScrollTop = ulElement.scrollHeight - ulElement.clientHeight;

    if (ulElement.scrollTop >= maxScrollTop - 10 && !this.reachedBottom) {
      this.loadMoreOptions();
      this.reachedBottom = true;
    }

    if (ulElement.scrollTop < maxScrollTop - 10) {
      this.reachedBottom = false;
    }
  }

  private mapQueryResultToOutlinedSelectOption(data: QueryResult[]): OutlinedSelectOption[] {
    return data.map((item: QueryResult) => ({
      text: item.name,
      value: item
    }));
  }

  private loadMoreOptions(): void {
    try {
      if (!this.hasMoreOptions || this.options.length < 10) {
        return;
      }

      this.optionsOffset += 10;

      this.searchFn(this.searchTerm, this.optionsLimit, this.optionsOffset)
        .pipe(takeUntil(this.removeSubscriptions))
        .subscribe((newOptions: QueryResult[]) => {
          if (newOptions.length < 10) {
            this.hasMoreOptions = false;
          }
          const optionsToBeAdded = this.mapQueryResultToOutlinedSelectOption(newOptions);
          this.options = this.options.concat(optionsToBeAdded);
        });
    } catch (err) {
      sentry.captureException(err);
    }
  }

  private resetValues(): void {
    this.optionsOffset = 0;
    this.hasMoreOptions = true;
  }
}
